import {
  COLLAPSIBLE_HEADER_CONTENT_TOP_PADDING,
  CollapsibleScreenHeader,
} from "@/components/prooftree/CollapsibleScreenHeader"
import { PressableScale } from "@/components/prooftree/PressableScale"
import { SkillChip } from "@/components/prooftree/SkillChip"
import { Box } from "@/components/ui/box"
import { HStack } from "@/components/ui/hstack"
import { Text } from "@/components/ui/text"
import { VStack } from "@/components/ui/vstack"
import { Colors } from "@/constants/Colors"
import {
  CoachAction,
  getCoachActionsForSkill,
  getContact,
  getDomain,
  getGoal,
  getSkill,
} from "@/data/prooftree"
import { listEqCoachActionsForSkill } from "@/services/eqParityBackend"
import { router, useLocalSearchParams } from "expo-router"
import {
  CalendarClock,
  ChevronRight,
  Clock3,
  Lightbulb,
  Sparkles,
  UserRound,
} from "lucide-react-native"
import { useEffect, useRef, useState } from "react"
import { Animated, Platform, TouchableOpacity } from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"

export default function CoachSkillScreen() {
  const { skillId } = useLocalSearchParams<{ skillId: string }>()
  const skill = getSkill(skillId)
  const domain = getDomain(skill.domainId)
  const color = domain?.color ?? Colors.dark.primary
  const actions = getCoachActionsForSkill(skill.id)
  const [savedActionCount, setSavedActionCount] = useState(0)
  const [syncMessage, setSyncMessage] = useState(
    "Using deterministic Coach templates until Supabase saved actions are available.",
  )
  const scrollY = useRef(new Animated.Value(0)).current

  useEffect(() => {
    let cancelled = false

    async function loadSavedActions() {
      const result = await listEqCoachActionsForSkill(skill.id)
      if (cancelled) return

      setSavedActionCount(result.mode === "supabase" ? result.items.length : 0)
      setSyncMessage(result.message)
    }

    loadSavedActions()

    return () => {
      cancelled = true
    }
  }, [skill.id])

  return (
    <SafeAreaView
      className="flex-1"
      edges={["top"]}
      style={{ backgroundColor: Colors.dark.surface }}
    >
      <CollapsibleScreenHeader
        scrollY={scrollY}
        eyebrow="Coach This Skill"
        title={`Suggested Actions for ${skill.title}`}
        subtitle="Pick one real action you can do now or soon."
        accentColor={color}
        backFallbackHref={`/skill/${skill.id}`}
        statusText={syncMessage}
      />
      <Animated.ScrollView
        className="flex-1"
        contentContainerStyle={{
          padding: 20,
          paddingTop: COLLAPSIBLE_HEADER_CONTENT_TOP_PADDING,
          paddingBottom: Platform.OS === "web" ? 48 : 32,
        }}
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={16}
        onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], {
          useNativeDriver: false,
        })}
      >
        <Box
          className="mb-5 rounded-3xl border p-5"
          style={{
            backgroundColor: `${Colors.dark.primary}1C`,
            borderColor: `${Colors.dark.primary}57`,
          }}
        >
          <HStack className="items-start gap-3">
            <Box
              className="h-11 w-11 items-center justify-center rounded-2xl"
              style={{ backgroundColor: `${Colors.dark.primary}29` }}
            >
              <Sparkles color={Colors.dark.primary} size={20} />
            </Box>
            <VStack className="flex-1 gap-2">
              <Text className="text-lg font-bold text-white">Proactive coaching mode</Text>
              <Text className="text-sm leading-5" style={{ color: Colors.dark.textPrimary }}>
                Log Proof is for something you already did. Coach Mode helps you choose the next
                practical rep, with a contact and goal when your data suggests a match.
              </Text>
              <Text className="text-xs font-bold" style={{ color: Colors.dark.primary }}>
                {syncMessage}
              </Text>
              <Text className="text-xs" style={{ color: Colors.dark.textSecondary }}>
                {savedActionCount} saved Supabase action{savedActionCount === 1 ? "" : "s"} for this
                skill.
              </Text>
              <SkillChip skillId={skill.id} compact />
            </VStack>
          </HStack>
        </Box>

        <VStack className="gap-4">
          {actions.map((action, index) => (
            <SuggestedActionCard
              key={action.id}
              action={action}
              color={color}
              primary={index === 0}
            />
          ))}
        </VStack>
      </Animated.ScrollView>
    </SafeAreaView>
  )
}

function SuggestedActionCard({
  action,
  color,
  primary,
}: {
  action: CoachAction
  color: string
  primary: boolean
}) {
  const contact = getContact(action.contactId)
  const goal = getGoal(action.goalId)

  return (
    <PressableScale
      onPress={() =>
        router.push({
          pathname: "/coach/action/[id]",
          params: { id: action.id },
        })
      }
    >
      <Box
        className="rounded-3xl border p-5"
        style={{
          backgroundColor: primary ? `${Colors.dark.primary}21` : Colors.dark.surfaceSecondary,
          borderColor: primary ? `${Colors.dark.primary}70` : `${color}44`,
          shadowColor: primary ? Colors.dark.primary : color,
          shadowOpacity: primary ? 0.18 : 0.08,
          shadowRadius: primary ? 18 : 10,
          shadowOffset: { width: 0, height: 10 },
        }}
      >
        <HStack className="mb-4 items-start gap-3">
          <Box
            className="h-11 w-11 items-center justify-center rounded-2xl"
            style={{ backgroundColor: `${color}22` }}
          >
            <Lightbulb color={color} size={20} />
          </Box>
          <VStack className="flex-1 gap-1">
            <Text className="text-xs font-bold" style={{ color }}>
              {action.timing} · {action.effort}
            </Text>
            <Text className="text-lg font-bold text-white">{action.title}</Text>
            <Text className="text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
              {action.description}
            </Text>
          </VStack>
          <ChevronRight color={color} size={20} />
        </HStack>

        <VStack className="gap-3">
          {contact ? (
            <TouchableOpacity
              activeOpacity={0.86}
              onPress={() =>
                router.push({
                  pathname: "/contact/[id]",
                  params: { id: contact.id },
                })
              }
            >
              <HStack
                className="items-start gap-2 rounded-2xl border p-3"
                style={{ borderColor: `${color}33` }}
              >
                <UserRound color={color} size={16} />
                <VStack className="flex-1 gap-1">
                  <Text className="text-xs font-bold" style={{ color }}>
                    Contact profile
                  </Text>
                  <Text className="text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
                    With <Text className="font-bold text-white">{contact.name}</Text>,{" "}
                    {contact.role}
                  </Text>
                </VStack>
                <ChevronRight color={color} size={18} />
              </HStack>
            </TouchableOpacity>
          ) : null}

          {goal ? (
            <HStack className="items-start gap-2">
              <CalendarClock color={color} size={16} />
              <Text
                className="flex-1 text-sm leading-5"
                style={{ color: Colors.dark.textSecondary }}
              >
                Goal match: <Text className="font-bold text-white">{goal.title}</Text>
              </Text>
            </HStack>
          ) : null}

          <HStack
            className="items-start gap-2 rounded-2xl border p-3"
            style={{ borderColor: `${color}33` }}
          >
            <Clock3 color={color} size={16} />
            <Text className="flex-1 text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
              {action.quickTip}
            </Text>
          </HStack>
        </VStack>
      </Box>
    </PressableScale>
  )
}
